import type { TypeSafeTableColumns } from "@dealer-center/core-components";
import {
  CheckBox,
  PageSize,
  Pagination,
  Table,
  Tooltip
} from "@dealer-center/core-components";
import type {
  DataGridProps,
  Result,
  TrimMapRow,
  TrimTableData,
  TrimWithOverrides,
  VehicleTableData
} from "@dealer-center/inventory-pages-gvm-interface";
import styled from "styled-components";
import type { ReactElement } from "react";
import { Link } from "react-router-dom";

const StyledDataGrid = styled.div`
  .data-grid {
    justify-content: center;
    text-align: center;
    min-width: 800px;
    width: 100%;
  }

  @media (max-width: 800px) {
    .data-grid {
      min-width: 400px;
    }
  }
`;

function renderQALabel(stage: number): ReactElement | null {
  return stage >= 30 && stage < 40 ? (
    <>
      <span>
        <b>QA</b>
      </span>
      {stage === 35 && (
        <span
          style={{
            fontSize: "6pt",
            position: "absolute"
          }}
        >
          <b>New</b>
        </span>
      )}
    </>
  ) : null;
}

function renderTrimOverrides(trimWO: TrimWithOverrides) {
  const hasOverrides =
    trimWO.hasOverrides && trimWO.trim.thirdPartyId === "DDC";

  if (hasOverrides) {
    const overrides = trimWO.diffs.map(diff => diff.fieldName).join("\n");
    return (
      <Tooltip
        position="right"
        size="large"
        sx={{ whiteSpace: "pre-line" }}
        toolTipContent={overrides}
      >
        <span>Yes</span>
      </Tooltip>
    );
  } else {
    return <span>No</span>;
  }
}

export const DataGrid = ({
  vehicleSearchResponse,
  stage,
  locale,
  max = 10,
  page = 1,
  selectedVehicles,
  setSelectedVehicles,
  selectedTrims,
  setSelectedTrims,
  onPagination,
  onPageResize
}: DataGridProps) => {
  const isQA = stage === "QA";
  const isLoading = false; // Keep this false for now
  const isElementDisabled = isLoading;

  const handleSelectAll = () => {
    if (!vehicleSearchResponse?.results) return;

    const allVehicleIds = vehicleSearchResponse.results.map(
      item => item.vehicle.globalVehicleId
    );
    const allTrimIds = vehicleSearchResponse.results.flatMap(
      item =>
        item.trimWithOverrides?.map(trim => trim.trim.globalVehicleTrimId) || []
    );

    const areAllSelected =
      selectedVehicles.length === allVehicleIds.length &&
      selectedTrims.length === allTrimIds.length;

    if (areAllSelected) {
      setSelectedVehicles([]);
      setSelectedTrims([]);
    } else {
      setSelectedVehicles(allVehicleIds);
      setSelectedTrims(allTrimIds);
    }
  };

  const isAllSelected = vehicleSearchResponse?.results
    ? selectedVehicles.length === vehicleSearchResponse.results.length &&
      selectedTrims.length ===
        vehicleSearchResponse.results.reduce(
          (sum, item) => sum + (item.trimWithOverrides?.length || 0),
          0
        )
    : false;

  // ************ Vehicle table ************
  const getVehicleColumns = (): TypeSafeTableColumns<VehicleTableData> => {
    return [
      {
        key: "check",
        dataIndex: "check",
        title: renderHeaderCheck(),
        columnSortable: false,
        width: 40
      },
      {
        key: "actions",
        dataIndex: "actions",
        title: "",
        columnSortable: false,
        width: 60
      },
      {
        key: "qa",
        dataIndex: "qa",
        title: "",
        columnSortable: false,
        width: 90
      },
      {
        key: "year",
        dataIndex: "year",
        title: "Year",
        columnSortable: false,
        align: "left"
      },
      {
        key: "make",
        dataIndex: "make",
        title: "Make",
        align: "left"
      },
      {
        key: "model",
        dataIndex: "model",
        title: "Model",
        align: "left"
      },
      {
        key: "bodyStyle",
        dataIndex: "bodyStyle",
        title: "Body Style",
        align: "left"
      }
    ];
  };

  const renderHeaderCheck = () => {
    return (
      isQA &&
      !!vehicleSearchResponse?.results && (
        <CheckBox
          aria-label="Select All"
          checked={isAllSelected}
          id="select-all"
          name="select-all"
          value="select-all"
          onChange={handleSelectAll}
        />
      )
    );
  };

  const handleVehicleSelect = (value: string) => {
    setSelectedVehicles(prev => {
      return prev.includes(value)
        ? prev.filter(id => id !== value)
        : [...prev, value];
    });
  };

  const renderVehicleCheck = (vehicleId: string) =>
    isQA && (
      <CheckBox
        checked={selectedVehicles.includes(vehicleId)}
        id={`vehicle-${vehicleId}`}
        name="vehicle"
        value={vehicleId}
        onChange={() => handleVehicleSelect(vehicleId)}
      />
    );

  const renderEditVehicle = (locale: string, vehicleId: string) => {
    return (
      <Link
        aria-disabled={isElementDisabled}
        rel="noopener noreferrer"
        to={`/gvm/vehicle?id=${vehicleId}&locale=${locale}`}
      >
        Edit
      </Link>
    );
  };

  const getVehicleTableData = (): VehicleTableData[] => {
    if (!vehicleSearchResponse) return [];
    return vehicleSearchResponse.results.map((item: Result, index: number) => ({
      check: renderVehicleCheck(item.vehicle.globalVehicleId),
      qa: renderQALabel(item.vehicle.stage),
      actions: renderEditVehicle(locale, item.vehicle.globalVehicleId),
      globalVehicleId: item.vehicle.globalVehicleId,
      year: item.vehicle.year,
      make: item.vehicle.make,
      model: item.vehicle.model,
      bodyStyle: item.vehicle.bodyStyle,
      index
    }));
  };

  // ************ Trim sub-table ************
  const getTrimColumns = (): TypeSafeTableColumns<TrimTableData> => {
    return [
      {
        key: "check",
        dataIndex: "check",
        title: "",
        columnSortable: false,
        width: 40
      },
      {
        key: "actions",
        dataIndex: "actions",
        title: "",
        columnSortable: false,
        width: 60
      },
      {
        key: "qa",
        dataIndex: "qa",
        title: "",
        columnSortable: false,
        width: 90
      },
      {
        key: "diffs",
        dataIndex: "diffs",
        title: "Overrides",
        sorter: (a: TrimTableData, b: TrimTableData, sortOrder) => {
          return sortOrder
            ? Number(a.hasOverrides) - Number(b.hasOverrides)
            : 0;
        }
      },
      { key: "trim", dataIndex: "trim", title: "Trim" },
      { key: "acode", dataIndex: "acodeAndPkgCode", title: "Acode (Pkg)" },
      { key: "body", dataIndex: "body", title: "CabType / DriveTrain" },
      { key: "transmission", dataIndex: "transmission", title: "Transmission" },
      { key: "msrp", dataIndex: "msrp", title: "MSRP" },
      { key: "idt", dataIndex: "idt", title: "IDT / Showroom" }
    ];
  };

  const getTrimMaps = (trimWO: TrimWithOverrides) => {
    const trimMapRow: TrimMapRow = {
      showroom: false,
      acode: "",
      packageCode: ""
    };

    trimWO.trim.trimMapList.forEach(trimMap => {
      if (
        "AUTODATA_SUPER" === trimMap.thirdPartyId ||
        "AUTODATA" === trimMap.thirdPartyId
      ) {
        trimMapRow.acode = trimMap.thirdPartyVehicleTrimId;
      }

      if ("PACKAGE_CODE" === trimMap.thirdPartyId) {
        trimMapRow.packageCode = trimMap.thirdPartyVehicleTrimId;
      }

      if ("IDT_SHOWROOM" === trimMap.thirdPartyId) {
        trimMapRow.showroom = true;
      }
    });

    return trimMapRow;
  };

  const vehicleColumns = getVehicleColumns();
  const vehicleData = getVehicleTableData();

  const handleTrimSelect = (globalVehicleTrimId: string) => {
    setSelectedTrims(prev => {
      return prev.includes(globalVehicleTrimId)
        ? prev.filter(id => id !== globalVehicleTrimId)
        : [...prev, globalVehicleTrimId];
    });
  };

  const renderTrimCheck = (trimId: string) =>
    isQA && (
      <CheckBox
        checked={!!selectedTrims && selectedTrims.includes(trimId)}
        id={trimId}
        name="trim"
        value={trimId}
        onChange={() => handleTrimSelect(trimId)}
      />
    );

  const renderEditTrim = (locale: string, trimId: string) => {
    return (
      <Link
        aria-disabled={isElementDisabled}
        rel="noopener noreferrer"
        to={`/gvm/trim?id=${trimId}&locale=${locale}`}
      >
        Edit
      </Link>
    );
  };

  const getTrimTableData = (index: number): TrimTableData[] => {
    const item = vehicleSearchResponse?.results[index];
    if (!item) return [];
    return item.trimWithOverrides.map((trimWO: TrimWithOverrides) => {
      const trimMaps: TrimMapRow = getTrimMaps(trimWO);
      const cabTypeAndDriveTrain =
        (trimWO.trim.cabType && trimWO.trim.cabType.trim().length > 0
          ? trimWO.trim.cabType + " / "
          : "") + trimWO.trim.driveTrain;
      const acodeAndPkgCode =
        trimMaps.acode +
        (trimMaps.packageCode.length > 0
          ? " (" + trimMaps.packageCode + ")"
          : "");

      return {
        check: renderTrimCheck(trimWO.trim.globalVehicleTrimId),
        qa: renderQALabel(trimWO.trim.stage),
        actions: renderEditTrim(locale, trimWO.trim.globalVehicleTrimId),
        diffs: renderTrimOverrides(trimWO),
        trim: trimWO.trim.uniqueTrim,
        acodeAndPkgCode,
        body: cabTypeAndDriveTrain,
        transmission: trimWO.trim.transmission,
        msrp: trimWO.trim.msrPrice
          ? trimWO.trim.msrPrice.toLocaleString("en-US")
          : "",
        idt: trimMaps.showroom ? "Yes" : "No",
        trimMaps,
        hasOverrides: trimWO.hasOverrides && trimWO.trim.thirdPartyId === "DDC"
      };
    });
  };

  const nestedTrimTable = (record: VehicleTableData) => {
    const trimColumns = getTrimColumns();
    const trimData = getTrimTableData(record.index);

    return (
      <Table.Table
        columns={trimColumns}
        data={trimData}
        data-testid={"gvm-trim-results"}
        dataDensity="small"
        emptyText="No trims found"
        enablePagination={false}
        id={`vehicle-with-trim-${record.globalVehicleId}`}
        sortableColumns={true}
        tableLayout={"auto"}
      />
    );
  };

  const doCount = () => {
    if (!vehicleSearchResponse) {
      return 0;
    }
    return Math.ceil(
      vehicleSearchResponse?.totalRecordCount / vehicleSearchResponse?.max
    );
  };

  const render = () => {
    return (
      <StyledDataGrid>
        {vehicleData.length > 0 && (
          <>
            <h3>Vehicle search results</h3>
            <div
              style={{
                display: "flex",
                paddingTop: "15px",
                justifyContent: "right",
                width: "100%"
              }}
            >
              <PageSize
                defaultPageSize={max}
                pageSizeOptions={[10, 20, 50, 100]}
                onShowSizeChange={onPageResize}
              />
            </div>
          </>
        )}
        <div className="data-grid">
          <Table.Table
            columns={vehicleColumns}
            currentPageSize={max}
            data={vehicleData}
            data-testid={"gvm-vehicle-results"}
            dataDensity="small"
            defaultExpandedRowKeys={[]}
            displayFilter={false}
            displayPageSizeSelector={false}
            emptyText="No vehicles found with the selected criteria"
            enablePagination={false}
            expandAllControl={"expanded"}
            expandedRowComponent={nestedTrimTable}
            highlightOnHover={false}
            id="TableFilter"
            pageSizePosition={"top"}
            scrollY={500}
            sortableColumns={true}
            tableLayout="auto"
          />
        </div>

        {vehicleData.length > 0 && (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              width: "100%"
            }}
          >
            <Pagination
              count={doCount()}
              defaultPage={1}
              page={page}
              showFirstButton={true}
              onChange={onPagination}
            />
          </div>
        )}
      </StyledDataGrid>
    );
  };

  return render();
};
